import { useEffect, useState } from 'react'
import type { Anomaly } from '../types'
import { fetchAnomalies } from '../lib/api'

const LEVEL_STYLES: Record<Anomaly['level'], string> = {
  critical: 'bg-red-500/20 border-red-500/40 text-red-100',
  warning:  'bg-amber-500/20 border-amber-500/40 text-amber-100',
}

const LEVEL_LABEL: Record<Anomaly['level'], string> = {
  critical: 'CRITICAL',
  warning:  'WARNING',
}

export function AnomalyBanner() {
  const [anomalies, setAnomalies] = useState<Anomaly[]>([])
  const [collapsed, setCollapsed] = useState(false)

  async function load() {
    try {
      setAnomalies(await fetchAnomalies())
    } catch {
      // Stay silent on transient RPC errors — this banner is supplementary,
      // not the primary signal. Repeated failures are visible elsewhere.
    }
  }

  useEffect(() => {
    load()
    const t = setInterval(load, 60_000)
    return () => clearInterval(t)
  }, [])

  if (anomalies.length === 0) return null

  const worst: Anomaly['level'] = anomalies.some((a) => a.level === 'critical')
    ? 'critical'
    : 'warning'

  return (
    <div className={`border-b ${LEVEL_STYLES[worst]}`}>
      <div className="max-w-[1600px] mx-auto px-6 py-2 flex items-start gap-3 text-sm">
        <span className="font-mono font-semibold shrink-0 mt-0.5">
          {LEVEL_LABEL[worst]} · {anomalies.length}
        </span>
        <button
          onClick={() => setCollapsed((c) => !c)}
          className="text-xs underline opacity-80 hover:opacity-100 mt-1 shrink-0"
        >
          {collapsed ? 'show' : 'hide'}
        </button>
        {!collapsed && (
          <ul className="space-y-1 grow">
            {anomalies.map((a, i) => (
              <li key={i} className="leading-snug">
                <span className="font-mono text-xs opacity-75">[{a.code}]</span>{' '}
                {a.message}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
